/**
 * useToast Hook
 * 
 * Custom hook for displaying toast notifications
 * with Alianzo design system styling
 * 
 * @example
 * ```tsx
 * const toast = useToast();
 * 
 * // Simple messages
 * toast.success('Usuário criado com sucesso!');
 * toast.error('Erro ao salvar dados');
 * toast.warning('Atenção: Verifique os campos');
 * toast.info('Novidade disponível');
 * 
 * // With description
 * toast.success('Operação concluída', 'Os dados foram salvos corretamente.');
 * 
 * // Loading state
 * const loadingId = toast.loading('Salvando...');
 * // Later...
 * toast.success('Salvo!', '', { id: loadingId });
 * 
 * // Promise-based
 * toast.promise(
 *   saveUser(),
 *   {
 *     loading: 'Salvando usuário...',
 *     success: 'Usuário salvo com sucesso!',
 *     error: 'Erro ao salvar usuário',
 *   }
 * );
 * ```
 */

import toast from 'react-hot-toast';
import type { ToastOptions } from '@/components/Common/Toast/Toast.types';

export const useToast = () => {
  /**
   * Show success toast
   */
  const success = (title: string, message?: string, options?: ToastOptions) => {
    return toast.success(
      message ? (
        <div>
          <div style={{ fontWeight: 600 }}>{title}</div>
          <div style={{ fontSize: '13px', marginTop: '4px', opacity: 0.9 }}>{message}</div>
        </div>
      ) : (
        title
      ),
      {
        duration: options?.duration || 4000,
        position: options?.position || 'top-right',
        id: options?.id,
      }
    );
  };

  /**
   * Show error toast
   */
  const error = (title: string, message?: string, options?: ToastOptions) => {
    return toast.error(
      message ? (
        <div>
          <div style={{ fontWeight: 600 }}>{title}</div>
          <div style={{ fontSize: '13px', marginTop: '4px', opacity: 0.9 }}>{message}</div>
        </div>
      ) : (
        title
      ),
      {
        duration: options?.duration || 5000,
        position: options?.position || 'top-right',
        id: options?.id,
      }
    );
  };

  /**
   * Show loading toast
   */
  const loading = (title: string, message?: string, options?: ToastOptions) => {
    return toast.loading(
      message ? (
        <div>
          <div style={{ fontWeight: 600 }}>{title}</div>
          <div style={{ fontSize: '13px', marginTop: '4px', opacity: 0.9 }}>{message}</div>
        </div>
      ) : (
        title
      ),
      {
        position: options?.position || 'top-right',
        id: options?.id,
      }
    );
  };

  /**
   * Show info toast (custom type)
   */
  const info = (title: string, message?: string, options?: ToastOptions) => {
    return toast(
      message ? (
        <div>
          <div style={{ fontWeight: 600 }}>{title}</div>
          <div style={{ fontSize: '13px', marginTop: '4px', opacity: 0.9 }}>{message}</div>
        </div>
      ) : (
        title
      ),
      {
        duration: options?.duration || 4000,
        position: options?.position || 'top-right',
        id: options?.id,
      }
    );
  };

  /**
   * Show promise-based toast
   * Automatically handles loading, success, and error states
   */
  const promise = <T,>(
    promiseFn: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: any) => string);
    },
    options?: ToastOptions
  ) => {
    return toast.promise(
      promiseFn,
      {
        loading: messages.loading,
        success: messages.success,
        error: messages.error,
      },
      {
        position: options?.position || 'top-right',
        id: options?.id,
      }
    );
  };

  /**
   * Dismiss specific toast or all toasts
   */
  const dismiss = (toastId?: string) => {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  };

  /**
   * Remove specific toast or all toasts instantly
   */
  const remove = (toastId?: string) => {
    if (toastId) {
      toast.remove(toastId);
    } else {
      toast.remove();
    }
  };

  return {
    success,
    error,
    loading,
    info,
    promise,
    dismiss,
    remove,
  };
};

export default useToast;
